import React from 'react';
import { Share2, Loader2, WifiOff, Clock, AlertTriangle, Server } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { t } from '../locales/nl';
import type { NetworkError } from '../utils/api';

interface FormButtonsProps {
  onSubmit: (_e: React.FormEvent) => void;
  onShare: () => void;
  isSubmitting: boolean;
  shareUrl: string | null;
  showSharePopover: boolean;
  setShowSharePopover: (_show: boolean) => void;
  submitError: NetworkError | null;
  submitProgress: number;
  hideShareButton?: boolean;
}

const getErrorIcon = (errorType: NetworkError['type']) => {
  switch (errorType) {
    case 'network':
      return <WifiOff className="h-5 w-5 text-red-400" />;
    case 'timeout':
      return <Clock className="h-5 w-5 text-orange-400" />;
    case 'server':
      return <Server className="h-5 w-5 text-red-400" />;
    case 'rate-limit':
      return <AlertTriangle className="h-5 w-5 text-yellow-400" />;
    default:
      return <AlertTriangle className="h-5 w-5 text-red-400" />;
  }
};

const getErrorStyle = (errorType: NetworkError['type']) => {
  switch (errorType) {
    case 'network':
      return 'bg-red-50 border-red-200 text-red-700';
    case 'timeout':
      return 'bg-orange-50 border-orange-200 text-orange-700';
    case 'server':
      return 'bg-red-50 border-red-200 text-red-700';
    case 'rate-limit':
      return 'bg-yellow-50 border-yellow-200 text-yellow-700';
    case 'client':
      return 'bg-blue-50 border-blue-200 text-blue-700';
    default:
      return 'bg-red-50 border-red-200 text-red-700';
  }
};

const FormButtons: React.FC<FormButtonsProps> = ({
  onSubmit,
  onShare,
  isSubmitting,
  shareUrl,
  showSharePopover,
  setShowSharePopover,
  submitError,
  submitProgress,
  hideShareButton = false
}) => {
  const isGeneratingReport = isSubmitting && submitProgress >= 100;

  return (
    <div className="flex flex-col space-y-3 mb-24">
      {submitError && (
        <div className={`border rounded-md p-4 mb-4 ${getErrorStyle(submitError.type)}`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {getErrorIcon(submitError.type)}
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium">{submitError.message}</p>
              {submitError.statusCode && (
                <p className="text-xs opacity-75 mt-1">
                  Status code: {submitError.statusCode}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      
      <button
        type="submit"
        disabled={isSubmitting}
        className="btn btn-primary w-full relative overflow-hidden"
        onClick={onSubmit}
      >
        <div 
          className="absolute inset-0 bg-white/30 transition-all duration-300"
          style={{ 
            transform: `translateX(${submitProgress - 100}%)`,
            opacity: isSubmitting ? 1 : 0
          }}
        />
        <div className="relative z-10 flex items-center justify-center">
          {isSubmitting ? (
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <div className="relative h-6 min-w-[200px] overflow-hidden">
                <div 
                  className="absolute inset-0 flex items-center justify-center whitespace-nowrap transition-all duration-300 ease-in-out"
                  style={{ 
                    transform: isGeneratingReport ? 'translateY(-100%)' : 'translateY(0)',
                    opacity: isGeneratingReport ? 0 : 1
                  }}
                >
                  {t.sending}
                </div>
                <div 
                  className="absolute inset-0 flex items-center justify-center whitespace-nowrap transition-all duration-300 ease-in-out"
                  style={{ 
                    transform: isGeneratingReport ? 'translateY(0)' : 'translateY(100%)',
                    opacity: isGeneratingReport ? 1 : 0
                  }}
                >
                  {t.generatingReport}
                </div>
              </div>
            </div>
          ) : (
            t.submit
          )}
        </div>
      </button>
      
      {!hideShareButton && (
        <Popover open={showSharePopover} onOpenChange={setShowSharePopover}>
          <PopoverTrigger asChild>
            <button
              type="button"
              onClick={onShare}
              className="btn btn-outline w-full flex items-center justify-center"
            >
              <Share2 size={16} className="mr-2" />
              {t.shareForm}
            </button>
          </PopoverTrigger>
          <PopoverContent align="center">
            <h3 className="text-sm font-medium text-blue-800 mb-2">{t.shareableLink}</h3>
            <p className="text-xs text-blue-600 break-all">{shareUrl}</p>
            <p className="text-xs text-blue-700 mt-2">{t.linkCopied}</p>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default FormButtons; 